<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
export type SelectedPCPOption = { id: string; name: string }

const emit = defineEmits<{ (e: 'pcp-selected', option: SelectedPCPOption): void }>()
const props = defineProps<{ product: FlattenedProduct; selectedPcpOptionId?: string }>()
const preferencesStore = usePreferencesStore()
const { pcpEnabled } = useFeatureFlags()
const locationId = inject('location', 'default').toLowerCase().replace(/ /g, '-')
provide('gaOptions', {
  group: 'professional care plan',
})

const mappedPrices = computed(() => mapPrices(props.product.tieredPrices ?? []))

const allPCPOptions = computed<ShopperProductsTypes.Option>(
  () =>
    props.product?.options?.find((option: ShopperProductsTypes.OptionValue) => option?.id === 'pcp') || {
      id: '',
      values: [],
    },
)
const pcpOptions = computed<ShopperProductsTypes.OptionValue[]>(() => {
  if (!mappedPrices.value?.retailPrice) {
    return []
  }
  return getProductCarePlans(mappedPrices.value.retailPrice, allPCPOptions.value?.values || [])
})

const defaultPcpId = computed(
  () =>
    pcpOptions.value?.find((option) => option?.id === props.selectedPcpOptionId)?.id ||
    pcpOptions.value?.find((option) => option?.default)?.id,
)
const selectedOptionId = ref(defaultPcpId.value)

const isShowPCP = computed(
  () => pcpEnabled() && props.product?.c_pcpEligibleProduct && props.product?.options && pcpOptions.value.length > 0,
)
</script>

<template>
  <template v-if="isShowPCP">
    <section class="pcp">
      <div class="pcp__header">
        <svg-icon name="mhj:pcp" />
        <h3 class="pcp__title">{{ $t('product_details_page.professional_care_plan.title') }}</h3>
      </div>
      <div class="pcp__description">
        <p>
          {{ allPCPOptions?.description || $t('product_details_page.professional_care_plan.callout') }}
          <Action class="pcp__learn-more" v-bind="preferencesStore.pdp?.pcpLink" />
        </p>
      </div>

      <RadioGroup
        :name="`pcp-options-${locationId}`"
        v-model="selectedOptionId"
        class="pcp__options"
        label="Choose a Plan"
      >
        <Radio
          v-for="option in pcpOptions"
          :name="`pcp-options-${locationId}`"
          v-model="selectedOptionId"
          :value="option.id"
          @select="$emit('pcp-selected', { id: selectedOptionId || '', name: option.name || '' })"
        >
          {{ option.name }}<span class="pcp__option-price" v-if="option.id !== 'PCPNO'">${{ option.price }}</span>
        </Radio>
      </RadioGroup>
    </section>
  </template>
</template>

<style lang="postcss">
.pcp {
  padding: var(--space-60);
  border: 1px solid var(--border-neutral-20);

  .pcp__header {
    display: flex;
    gap: var(--space-30);
    align-items: center;
    margin-bottom: var(--space-50);

    svg {
      height: 1.875rem;
    }

    .pcp__title {
      margin: 0;
    }
  }

  .pcp__description {
    p {
      margin-bottom: 0;
    }
    .pcp__learn-more {
      font-size: var(--text-small);
      text-transform: unset;
    }
  }

  .pcp__options {
    margin-top: var(--space-60);
  }

  .pcp__option-price {
    margin-left: var(--space-20);
    color: var(--text-secondary);
  }
}
</style>
